import * as Sentry from '@sentry/react'

declare const __GIT_HASH__: string

export function initSentry() {
  if (import.meta.env.DEV) return

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development',
    release: __GIT_HASH__,

    tracesSampleRate: 1.0,

    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,

    sendDefaultPii: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
      Sentry.replayCanvasIntegration(),
    ],
  })
}

let dayFlowSessionMarked = false

// TEMPORARY observation window: record every wheel session in full so we can
// watch how the wheel performs. It wins over the day-flow replay-off policy
// below whichever effect runs first (markDayFlowSession leaves a live wheel
// recording alone; markWheelSession restarts one day-flow stopped), and it
// self-expires — after WHEEL_REPLAY_UNTIL it does nothing and the wheel is just
// another day-flow session again. Remove this, its guard in markDayFlowSession
// and the WheelScreen call once the window closes.
const WHEEL_REPLAY_UNTIL = Date.parse('2026-11-03T00:00:00Z')
let wheelReplayActive = false

// Tag the session as wheel (filterable) and, inside the window, force a full
// Session Replay regardless of the 1% sample or the day-flow stop. No-op in dev
// (Sentry isn't initialised) and idempotent per page load.
export function markWheelSession() {
  Sentry.setTag('wheel', true)
  if (Date.now() >= WHEEL_REPLAY_UNTIL || wheelReplayActive) return
  wheelReplayActive = true

  const replay = Sentry.getReplay()
  if (!replay) return
  // flush() persists one already buffering (an on-error buffer or a sampled
  // session); start() begins one when nothing is recording.
  if (replay.getReplayId()) replay.flush()
  else replay.start()
}

// Tag the session as day-flow (so events stay filterable by the `day_flow`
// tag) and switch Session Replay off for it: day-flow sessions are never
// recorded — neither the sampled session nor an on-error buffer — so `stop()`
// tears down whatever is currently recording or buffering. No-op in dev
// (Sentry isn't initialised) and idempotent per page load.
export function markDayFlowSession(planType?: string | null) {
  Sentry.setTag('day_flow', true)
  if (planType) Sentry.setTag('day_flow_plan', planType)

  if (dayFlowSessionMarked) return
  dayFlowSessionMarked = true

  // The wheel is recording in full for its observation window — leave it be.
  if (wheelReplayActive) return

  const replay = Sentry.getReplay()
  if (!replay) return
  replay.stop()
}

export function setSentryUser(userId: string) {
  Sentry.setUser({ id: userId })
}

export function clearSentryUser() {
  Sentry.setUser(null)
}
